import React, { useState, useEffect } from 'react';
import { auth, db } from '../lib/firebase';
import { 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Dashboard from './Dashboard';

function Auth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check for redirect result first
    getRedirectResult(auth)
      .then(async (result) => {
        if (result && result.user) {
          console.log('Redirect sign-in successful');
          await handleUserCreation(result.user);
        }
      })
      .catch((error) => {
        console.error('Redirect error:', error);
        setError('Sign in failed. Please try again.');
      });

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        await handleUserCreation(user);
      }
      setUser(user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleUserCreation = async (user) => {
    // Check if user document exists
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (!userDoc.exists()) {
      // Create user document if it doesn't exist
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: user.displayName || 'Anonymous',
        photoURL: user.photoURL || null,
        createdAt: new Date(),
        emailVerified: user.emailVerified,
        provider: user.providerData[0]?.providerId || 'email'
      });
    }
  };

  const signInWithGoogle = async () => {
    setError(''); // Clear any existing errors
    const provider = new GoogleAuthProvider();
    
    // Add this to prevent account selection issues
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    try {
      // Try popup first
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Popup error:', error);
      
      if (error.code === 'auth/popup-blocked' || 
          error.code === 'auth/popup-closed-by-user' ||
          error.code === 'auth/cancelled-popup-request') {
        // Fallback to redirect method
        try {
          await signInWithRedirect(auth, provider);
        } catch (redirectError) {
          console.error('Redirect error:', redirectError);
          setError('Sign in failed. Please try again.');
        }
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized. Please contact support.');
      } else {
        setError(error.message);
      }
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      if (isSignUp) {
        // Sign up
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        
        // Update display name
        await updateProfile(userCredential.user, {
          displayName: name
        });
        
        // Send verification email
        await sendEmailVerification(userCredential.user);
        
        // Create user document in Firestore
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          email: userCredential.user.email,
          displayName: name,
          createdAt: new Date(),
          emailVerified: false
        });
        
        alert('Verification email sent! Please check your inbox.');
        
      } else {
        // Sign in
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  if (loading) {
    return (
      <div className="auth-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (user) {
    return <Dashboard />;
  }

  return (
    <div className="auth-container">
      <div className="auth-bg"></div>
      <div className="auth-card">
        <h1 className="auth-logo">Scalesman</h1>
        <h2>{isSignUp ? 'Create Account' : 'Welcome Back'}</h2>
        <p>{isSignUp ? 'Sign up to start building amazing apps' : 'Sign in to continue building amazing apps'}</p>
        
        <form onSubmit={handleEmailAuth} className="auth-form">
          {isSignUp && (
            <input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="auth-input"
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="auth-input"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength="6"
            className="auth-input"
          />
          
          {error && <p className="auth-error">{error}</p>}
          
          <button type="submit" className="auth-submit-btn">
            {isSignUp ? 'Sign Up' : 'Sign In'}
          </button>
        </form>
        
        <div className="auth-divider">
          <span>OR</span>
        </div>
        
        <button onClick={signInWithGoogle} className="google-signin-btn">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
          Continue with Google
        </button>
        
        <p className="auth-switch">
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}
          <button 
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError('');
            }} 
            className="auth-switch-btn"
          >
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </button>
        </p>
      </div>
    </div>
  );
}

export default Auth;